import type { ResultColumn, ResultEnvelope, Row, Visualization } from "@cd/core";
import { distinctValues, groupLabel } from "./echarts";

/** A grouping value a click landed on: its result column and the raw value (not its label). */
export interface PickedValue {
  column: ResultColumn;
  value: Row[string];
}

/** The fields of an ECharts click event that `chartPick` reads. */
export interface ChartClick {
  seriesName?: string;
  dataIndex?: number;
  name?: string;
  data?: unknown;
  /** The data item's value: a heatmap cell's [x, y, value] even when its item is an object (a dimmed cell). */
  value?: unknown;
  treePathInfo?: { name: string }[];
}

/**
 * Whether a page filter can be made from this column. A time grain such as day of week can't be
 * expressed as a filter on its field; date buckets zoom instead (`canZoomInto`).
 */
export const canFilterBy = (c: ResultColumn) => c.kind === "dimension" && c.type !== "time" && !c.grain;

/** Whether a click on this column's values can zoom the page's dates: a bucket of days, not an hour or a weekday. */
export const canZoomInto = (c: ResultColumn) =>
  c.kind === "dimension" && c.type === "time" && !!c.grain && ["day", "week", "month", "quarter", "year"].includes(c.grain);

/** Whether a click on this column's values does anything. */
export const canPick = (c: ResultColumn) => canFilterBy(c) || canZoomInto(c);

/**
 * The raw value whose label is `text`, or undefined when no row has it: "Other", which folds
 * several values together, is never a pick.
 */
function byLabel(rows: Row[], col: ResultColumn, text: string | undefined): PickedValue | undefined {
  if (text === undefined) return undefined;
  const row = rows.find((r) => groupLabel(col, r[col.key] ?? null) === text);
  return row ? { column: col, value: row[col.key] ?? null } : undefined;
}

/**
 * The grouping values under a click on a chart built by `buildChartOption`, mapped back from the
 * labels and indexes ECharts reports to the result's raw values. Mirrors how each chart type lays
 * out its rows; empty when the click wasn't on a grouping value.
 */
export function chartPick(viz: Visualization, res: ResultEnvelope, e: ChartClick): PickedValue[] {
  const dims = res.columns.filter((c) => c.kind === "dimension");
  const rows = res.rows;
  const picks = (...p: (PickedValue | undefined)[]) => p.filter((x): x is PickedValue => x !== undefined);
  switch (viz.type) {
    case "funnel":
    case "kpi":
    case "table":
    case "pivot":
      return [];
    case "pie":
      return dims[0] ? picks(byLabel(rows, dims[0], e.name)) : [];
    case "heatmap": {
      const [x, y] = dims;
      const cell = Array.isArray(e.value) ? (e.value as number[]) : Array.isArray(e.data) ? (e.data as number[]) : undefined;
      if (!x || !y || !cell) return [];
      const at = (col: ResultColumn, i: number | undefined) => {
        const vals = distinctValues(rows, col, true);
        return i !== undefined && i < vals.length ? { column: col, value: (vals[i] ?? null) as Row[string] } : undefined;
      };
      return picks(at(x, cell[0]), at(y, cell[1]));
    }
    case "scatter": {
      const [point, group] = dims;
      if (!point) return [];
      return picks(byLabel(rows, point, e.name), group ? byLabel(rows, group, e.seriesName) : undefined);
    }
    case "treemap": {
      const [outer, inner] = dims;
      // The path starts at the (unnamed) root.
      const path = e.treePathInfo?.slice(1).map((n) => n.name) ?? [];
      if (!outer || !path.length) return [];
      return picks(byLabel(rows, outer, path[0]), inner ? byLabel(rows, inner, path[1]) : undefined);
    }
    default: {
      // Line, area, bar and combo: categories along x in first-seen order, an optional second
      // grouping as one series each.
      const [x, series] = dims;
      if (!x || e.dataIndex === undefined) return [];
      const seen = new Map<string, Row[string]>();
      for (const r of rows) {
        const k = String(r[x.key]);
        if (!seen.has(k)) seen.set(k, r[x.key] ?? null);
      }
      const xs = [...seen.values()];
      const xPick = e.dataIndex < xs.length ? { column: x, value: xs[e.dataIndex]! } : undefined;
      return picks(xPick, series ? byLabel(rows, series, e.seriesName) : undefined);
    }
  }
}
